import { useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';
import { TextInput } from '../../shared/input/Input';

interface Props<T, K> {
    label: string | React.ReactNode;
    icon?: React.ReactNode;
    options: T;
    onOption?: (key: K) => void;
    id?: string;
    className?: string;
}

export default function Select<T extends Record<K, string>, K extends keyof T>({
    label,
    icon,
    options,
    onOption,
    id,
    className,
}: Props<T, K>) {
    const [show, setShow] = useState(false);
    const menuRef = useRef<HTMLUListElement>(null);
    const [filter, setFilter] = useState('');

    function onClick(key: K) {
        setShow((prev) => !prev);
        onOption?.(key);
    }

    function mouseMove(e: MouseEvent) {
        // Auto close sort menu when mouse wanders too far
        const menu = menuRef.current;
        if (!menu) return;
        const rect = menu.getBoundingClientRect();
        const dist = 100;

        if (
            e.clientX < rect.x - dist ||
            e.clientX > rect.x + rect.width + dist ||
            e.clientY < rect.y - dist ||
            e.clientY > rect.y + rect.height + dist
        ) {
            setShow(false);
        }
    }

    useEventListener('mousemove', mouseMove);

    return (
        <>
            {show && <div className='absolute inset-0 z-30' onClick={() => setShow(false)} />}
            <div className={'relative inline-block' + (className ? ` ${className}` : '')} id={id}>
                <button
                    type='button'
                    className={
                        'flex h-9 items-center gap-2 round:rounded-lg border px-3 pe-8 transition-all w-full ' +
                        (show
                            ? 'border-theme-primary bg-theme-primary/10 text-theme-text ring-2 ring-theme-primary/20'
                            : 'border-theme-outline bg-theme-950/70 text-theme-text hover:border-theme-300 hover:bg-theme-900/80')
                    }
                    onClick={() => setShow((prev) => !prev)}
                >
                    {icon}
                    <span className='flex min-w-0 items-center truncate'>{label}</span>
                    <i className='bx bx-caret-down absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none' />
                </button>
                <ul
                    ref={menuRef}
                    className={
                        'absolute z-40 mt-1 w-max min-w-full overflow-hidden round:rounded-lg border border-theme-outline bg-theme-700 p-1 shadow-2xl transition-all ' +
                        (show ? 'opacity-100' : 'opacity-0 pointer-events-none')
                    }
                >
                    {Object.entries(options).length > 5 && (
                        <li className='p-1'>
                            <TextInput
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder='Search...'
                            />
                        </li>
                    )}
                    {Object.entries(options)
                        .filter(([_, value]) => (value as string).toLowerCase().includes(filter.toLowerCase()))
                        .map(([key, value]) => (
                            <li key={key}>
                                <button
                                    className='w-full round:rounded-md px-3 py-1.5 text-start transition-colors hover:bg-theme-600'
                                    onClick={() => onClick(key as K)}
                                    type='button'
                                >
                                    {value as string}
                                </button>
                            </li>
                        ))}
                </ul>
            </div>
        </>
    );
}
